import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import {
  calculateDocumentSubtotal,
  getPublicQuoteSample,
} from '../data/publicDocumentSamples';

const formatCurrency = (amount: number, currencyCode: string) =>
  amount.toLocaleString('fr-FR', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  });

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const QuoteDocument = () => {
  const { quoteNumber } = useParams();
  const quote = getPublicQuoteSample(quoteNumber);
  const subtotal = calculateDocumentSubtotal(quote.items);
  const total = subtotal + quote.taxAmount;

  return (
    <>
      <Helmet>
        <title>{quote.quoteNumber} - Devis | Couteau Suisse Digital</title>
        <meta
          name="description"
          content="Consultez votre devis Couteau Suisse Digital, son périmètre et les prochaines étapes pour validation."
        />
      </Helmet>

      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="overflow-hidden rounded-3xl bg-white shadow-xl"
          >
            <div className="border-b border-[#f1ece8] bg-gradient-to-b from-[#f7f3ef] to-[#fffaf7] px-8 py-10">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-neon-orange">
                Devis
              </div>
              <h1 className="mt-3 text-4xl font-bold text-gray-900">{quote.quoteNumber}</h1>
              <p className="mt-4 max-w-2xl text-lg text-gray-600">
                Proposition préparée pour <strong>{quote.clientName}</strong> concernant{' '}
                <strong>{quote.serviceName}</strong>.
              </p>
            </div>

            <div className="grid gap-8 px-8 py-8 lg:grid-cols-[minmax(0,1.2fr)_320px]">
              <div className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900">Coordonnées</h2>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="font-semibold text-gray-900">{quote.clientName}</div>
                      <div>{quote.clientCompany}</div>
                      <div>{quote.clientEmail}</div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900">Cadre du devis</h2>
                    <div className="space-y-3 text-sm text-gray-700">
                      <div className="flex justify-between gap-4">
                        <span>Date</span>
                        <strong>{formatDate(quote.quoteDate)}</strong>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Validité</span>
                        <strong>{formatDate(quote.quoteValidUntil)}</strong>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Prestation</span>
                        <strong className="text-right">{quote.serviceName}</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white p-6">
                  <h2 className="mb-4 text-xl font-semibold text-gray-900">Périmètre</h2>
                  <ul className="space-y-3 text-sm leading-6 text-gray-600">
                    {quote.scope.map((scopeItem) => (
                      <li key={scopeItem} className="flex items-start gap-3">
                        <span className="mt-1 h-2 w-2 rounded-full bg-neon-orange" />
                        <span>{scopeItem}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="overflow-hidden rounded-2xl border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-[#fff4eb]">
                      <tr className="text-left text-sm font-semibold text-[#7c4a22]">
                        <th className="px-6 py-4">Description</th>
                        <th className="px-6 py-4 text-center">Qté</th>
                        <th className="px-6 py-4 text-right">Prix</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {quote.items.map((item) => (
                        <tr key={item.label}>
                          <td className="px-6 py-4 text-sm text-gray-900">{item.label}</td>
                          <td className="px-6 py-4 text-center text-sm text-gray-700">
                            {item.quantity}
                          </td>
                          <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                            {formatCurrency(item.quantity * item.unitPrice, quote.currencyCode)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-5">
                <div className="rounded-2xl border border-[#f6d8c0] bg-[#fff4eb] p-6">
                  <div className="space-y-3 text-sm text-gray-700">
                    <div className="flex justify-between gap-4">
                      <span>Sous-total</span>
                      <strong>{formatCurrency(subtotal, quote.currencyCode)}</strong>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>Taxes</span>
                      <strong>{formatCurrency(quote.taxAmount, quote.currencyCode)}</strong>
                    </div>
                    <div className="flex justify-between gap-4 text-base">
                      <span>Total</span>
                      <strong className="text-neon-orange">
                        {formatCurrency(total, quote.currencyCode)}
                      </strong>
                    </div>
                  </div>
                </div>

                <div id="accepter" className="rounded-2xl border border-gray-200 bg-white p-6">
                  <h3 className="mb-3 text-lg font-semibold text-gray-900">Validation du devis</h3>
                  <p className="text-sm leading-6 text-gray-600">
                    Si tu souhaites valider ce devis, demander un ajustement ou préparer le
                    règlement, notre équipe peut finaliser cela avec toi rapidement.
                  </p>
                  <div className="mt-5 flex flex-col gap-3">
                    <Link
                      to="/contact"
                      className="inline-flex items-center justify-center rounded-full bg-neon-orange px-6 py-4 font-semibold text-white transition-colors hover:bg-[#FF4500]"
                    >
                      Demander la validation
                    </Link>
                    <Link
                      to="/reserver"
                      className="inline-flex items-center justify-center rounded-full border border-neon-orange px-6 py-4 font-semibold text-neon-orange transition-colors hover:bg-neon-orange hover:text-white"
                    >
                      Réserver un échange
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>
        </div>
      </main>
    </>
  );
};

export default QuoteDocument;
